import React, { useEffect, useState } from 'react';
import { dealsAPI, leadsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Badge, Button, Modal, FormField, Table, Empty, Alert, SegmentedControl, Loading, FilterPills, SearchInput } from '../components/ui';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const empty = { title: '', value: '', stage: 'Prospect', lead_id: '', closing_date: '' };

const STAGE_ORDER = ['Prospect', 'Proposal', 'Negotiation', 'Won', 'Lost'];

export default function Deals() {
  const { canCreate, canUpdate, canDelete } = useAuth();
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('table'); // 'table' | 'kanban'
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([dealsAPI.list(), leadsAPI.list()])
      .then(([d, l]) => { setDeals(d.data.data || []); setLeads(l.data.data || []); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setError(''); setModal(true); };
  const openEdit = (d) => { setEditing(d); setForm({ title: d.title, value: d.value||'', stage: d.stage, lead_id: d.lead_id, closing_date: d.closing_date||'' }); setError(''); setModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (editing) await dealsAPI.update(editing.deal_id, form);
      else await dealsAPI.create(form);
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    await dealsAPI.delete(id); load();
  };

  const totalValue = deals.filter(d => d.stage === 'Won').reduce((s, d) => s + parseFloat(d.value || 0), 0);
  
  const searchFilteredDeals = deals.filter(d => {
    if (!search) return true;
    const lower = search.toLowerCase();
    return d.title?.toLowerCase().includes(lower) || d.lead?.name?.toLowerCase().includes(lower);
  });
  
  const filtered = filter ? searchFilteredDeals.filter(d => d.stage === filter) : searchFilteredDeals;

  const columns = [
    { key: 'title', label: 'Deal', render: r => <span style={{ fontWeight: 600 }}>{r.title}</span> },
    { key: 'lead', label: 'Lead', render: r => r.lead?.name || '—' },
    { key: 'value', label: 'Value', render: r => r.value ? `$${parseFloat(r.value).toLocaleString()}` : '—' },
    { key: 'stage', label: 'Stage', render: r => <Badge value={r.stage} /> },
    { key: 'closing_date', label: 'Closing', render: r => r.closing_date || '—' },
    { key: 'actions', label: '', render: r => (canUpdate || canDelete) ? (
      <div style={{ display: 'flex', gap: 8 }}>
        {canUpdate && <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(r); }}><Pencil size={13} /></Button>}
        {canDelete && <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); handleDelete(r.deal_id); }}><Trash2 size={13} /></Button>}
      </div>
    ) : null}
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="Deals"
        subtitle={`${deals.length} deals · $${totalValue.toLocaleString()} won`}
        eyebrow="Revenue"
        action={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search deals..." />
            <SegmentedControl
              options={[{ value: 'table', label: 'Table' }, { value: 'kanban', label: 'Kanban' }]}
              value={view}
              onChange={setView}
            />
            {canCreate && <Button onClick={openCreate}><Plus size={15} />New Deal</Button>}
          </div>
        }
      />

      <FilterPills
        options={[
          { value: '', label: 'All' },
          ...STAGE_ORDER.map(s => ({ value: s, label: s })),
        ]}
        value={filter}
        onChange={setFilter}
      />

      {view === 'table' ? (
        <Card>
          {loading ? <Loading />
            : filtered.length === 0 ? <Empty message="No deals found" />
            : <Table columns={columns} data={filtered} />}
        </Card>
      ) : (
        <div className="ui-kanban">
          {STAGE_ORDER.map((stage) => {
            const stagDeals = searchFilteredDeals.filter((d) => d.stage === stage);
            return (
              <div key={stage} className="ui-kanban-col">
                <div className="ui-kanban-col__header">
                  <span className="ui-kanban-col__title">{stage}</span>
                  <Badge value={String(stagDeals.length)} />
                </div>
                <div>
                  {stagDeals.map((d) => (
                    <div key={d.deal_id} className="ui-kanban-card">
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{d.title}</div>
                      {d.lead?.name && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{d.lead.name}</div>}
                      {d.value && <div style={{ fontSize: 14, color: 'var(--accent3)', fontWeight: 700 }}>${parseFloat(d.value).toLocaleString()}</div>}
                      {(canUpdate || canDelete) && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                          {canUpdate && <Button size="sm" variant="ghost" onClick={() => openEdit(d)}><Pencil size={11} /></Button>}
                          {canDelete && <Button size="sm" variant="danger" onClick={() => handleDelete(d.deal_id)}><Trash2 size={11} /></Button>}
                        </div>
                      )}
                    </div>
                  ))}
                  {stagDeals.length === 0 && (
                    <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-dim)', fontSize: 12, border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)' }}>
                      Empty
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Deal' : 'New Deal'}>
        <Alert type="error" message={error} />
        <form onSubmit={handleSave}>
          <FormField label="Deal Title"><input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} required placeholder="Deal title" /></FormField>
          <FormField label="Lead">
            <select value={form.lead_id} onChange={e => setForm(f => ({...f, lead_id: e.target.value}))} required>
              <option value="">Select lead...</option>
              {leads.map(l => <option key={l.lead_id} value={l.lead_id}>{l.name}</option>)}
            </select>
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormField label="Value ($)"><input type="number" value={form.value} onChange={e => setForm(f => ({...f, value: e.target.value}))} placeholder="0.00" min="0" step="0.01" /></FormField>
            <FormField label="Stage">
              <select value={form.stage} onChange={e => setForm(f => ({...f, stage: e.target.value}))}>
                {STAGE_ORDER.map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Closing Date"><input type="date" value={form.closing_date} onChange={e => setForm(f => ({...f, closing_date: e.target.value}))} /></FormField>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setModal(false)} type="button">Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
